import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";

export default function MeetingSummarizeConfirmDialog({
  open,
  onClose,
  onConfirm,
  regenerating = false,
  audioCount = 0,
  photoCount = 0,
  videoCount = 0,
  fullScreen = false,
}) {
  const { t } = useTranslation();
  const mediaTotal = Number(photoCount || 0) + Number(videoCount || 0);
  const heavy = mediaTotal > 20;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
    >
      <DialogTitle sx={{ pr: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "warning.main",
              color: "warning.contrastText",
              flexShrink: 0,
            }}
          >
            <PaidOutlinedIcon fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
            {regenerating
              ? t("Meetings.summarizeConfirmTitleRegen")
              : t("Meetings.summarizeConfirmTitle")}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={1.75}>
          <Alert severity="warning" variant="outlined" icon={false}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("Meetings.summarizeConfirmCostTitle")}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
              {t("Meetings.summarizeConfirmCostBody")}
            </Typography>
          </Alert>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.45 }}
          >
            {regenerating
              ? t("Meetings.summarizeConfirmRegenBody")
              : t("Meetings.summarizeConfirmBody")}
          </Typography>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {t("Meetings.summarizeConfirmMedia")}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.35 }}>
              {t("Meetings.counts", {
                audio: audioCount || 0,
                photos: photoCount || 0,
                videos: videoCount || 0,
              })}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1, lineHeight: 1.4 }}
            >
              {heavy
                ? t("Meetings.summarizeConfirmTimeHeavy")
                : t("Meetings.summarizeConfirmTime")}
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary">
            {t("Meetings.summarizeConfirmKeepMedia")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          "& > :not(style)": { m: 0 },
        }}
      >
        <Button fullWidth={fullScreen} onClick={onClose}>
          {t("Meetings.cancel")}
        </Button>
        <Button
          fullWidth={fullScreen}
          variant="contained"
          color="warning"
          startIcon={<AutoAwesomeOutlinedIcon />}
          onClick={onConfirm}
          sx={{ minHeight: 48, fontWeight: 800 }}
        >
          {regenerating
            ? t("Meetings.summarizeConfirmYesRegen")
            : t("Meetings.summarizeConfirmYes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
